import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { fetchOrder } from 'entities/Order';
import { getItemSavingsResult } from 'entities/Order/lib/calculateOrderSavings/calculateOrderSavings';
import { OrderStatuses } from 'entities/Order/model/consts/orderStatuses';
import { getCanEditCountOfQty } from 'entities/Order/model/selectors/ordersPermissions';
import {
    getIsOrderSubmissionLocked,
    getSelectedOrder,
    getSelectedOrderId,
    getSelectedOrderIsPreSubmission,
    getSelectedOrderItems,
    getSelectedOrderStatus,
} from 'entities/Order/model/selectors/ordersSelectors';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import { useGetItemDataUpdate } from 'features/CatalogItemCheckbox/lib/useGetItemDataUpdate';
import { RemoveItemFromOrderRef } from 'features/RemoveItemFromOrder/ui/RemoveItemFromOrder/RemoveItemFromOrder';
import { editOrderItem } from 'features/editItemsInOrder/model/services/editOrderItem/editOrderItem';
import { formatNumber } from 'shared/lib/formatNumber/formatNumber';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useDebouncedCallback } from 'shared/lib/hooks/useDebouncedCallback';
import { useInOrderSavingsVisible } from 'shared/lib/hooks/useInOrderSavingsVisible';
import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { OrderItemQuantityPicker } from 'shared/ui/OrderItemQuantityPicker';
import { OrderPriceDisplay } from 'shared/ui/OrderPriceDisplay/OrderPriceDisplay';
import { ProductRowTitleBlock } from 'shared/ui/ProductRowTitleBlock';
import { DynamicTooltip } from 'shared/ui/Tooltip';
import { useOrderItemHooks } from 'widgets/OrderGroupedItems/hooks/OrderItemHooks';

import { OrderGroupItemsTableRowSubstitutions } from './OrderGroupedItemsTableRowSubstitutions';
import { OrderGroupItemsTableRowSuppliers } from './OrderGroupedItemsTableRowSuppliers';
import { OrderItemSavings } from './OrderItemSavings';

import type { TFunction } from 'i18next';

const customSourcingTooltipContent = (t: TFunction) => {
    return t(
        // eslint-disable-next-line max-len
        "Adjusting the total quantity will overwrite the changes made to individual supplier quantities, using the property's sourcing preferences.",
    );
};

const skeletonSxProps = {
    '&::after': {
        animationDuration: '1.5s',
        background: 'linear-gradient(90deg,transparent,#DCDCDC,transparent)',
    },
    backgroundColor: '#F1F1F1',
    borderRadius: '6px',
};

interface OrderGroupedItemsTableRowsProps {
    item: CatalogItem;
    onOpenProductDetails: (item: CatalogItem) => void;
}

export const OrderGroupedItemsTableRow = ({
    item,
    onOpenProductDetails,
}: OrderGroupedItemsTableRowsProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const removeItemRef = useRef<RemoveItemFromOrderRef>(null);

    const canSeePrices = useSelector(getUserCanSeePrices);
    const canEditQty = useSelector(getCanEditCountOfQty);
    const selectedOrderId = useSelector(getSelectedOrderId);
    const selectedOrder = useSelector(getSelectedOrder);
    const selectedOrderStatus = useSelector(getSelectedOrderStatus);
    const selectedOrderItems = useSelector(getSelectedOrderItems);
    const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);
    const isMaintenanceTechnician = useSelector(getUserIsMaintenanceTechnician);
    const {
        allSuppliersSubstituted,
        allSuppliersUnavailable,
        substitutionTitle,
        totalQtyFulfilled,
        totalPriceFulfilled,
    } = useOrderItemHooks(item);
    const isOrderSubmissionLocked = useSelector(getIsOrderSubmissionLocked);

    const itemOpacity =
        allSuppliersSubstituted || allSuppliersUnavailable ? 0.35 : 1;

    const [itemUpdateInProgress, setItemUpdateInProgress] = useState(false);
    const [inputQuantity, setInputQuantity] = useState<number | null>(null);

    const inOrderSavingsVisible = useInOrderSavingsVisible();
    const showInventory = !isMaintenanceTechnician && isPreSubmission;
    const showPrices = canSeePrices && !isMaintenanceTechnician;
    const showPicker = isPreSubmission && canEditQty;
    const suppressStatusesForTechs = [
        OrderStatuses.DRAFT,
        OrderStatuses.IN_REVIEW,
        OrderStatuses.PENDING_APPROVAL,
    ];

    const showSellers =
        !isMaintenanceTechnician ||
        !(
            isMaintenanceTechnician &&
            suppressStatusesForTechs.includes(selectedOrderStatus)
        );

    // Get the original order item to access allCheckResults
    const orderItem = selectedOrderItems?.find(
        (orderItem) => orderItem.id === item.id,
    );

    const itemSavingsResult = orderItem
        ? getItemSavingsResult(orderItem)
        : null;

    const { qty: itemQuantity } = useGetItemDataUpdate({
        item,
    });

    const { openLightbox } = useImageLightbox();

    useEffect(() => {
        setInputQuantity(itemQuantity);
    }, [itemQuantity]);

    const areChecksRunning = !item?.isCheckFinished;
    const hasActiveSelectedProposals = item?.selectedProposals?.some(
        (proposal) => proposal.selectedQuantity > 0,
    );

    // If we have selected proposals, calculate the total price from the selected proposals
    // Otherwise, calculate the total price from the lowest sell price from the proposals
    let totalPrice = 0;
    if (hasActiveSelectedProposals) {
        totalPrice = item?.selectedProposals?.reduce(
            (acc, proposal) => acc + proposal.price * proposal.selectedQuantity,
            0,
        );
    } else {
        const activeProposals =
            item?.proposals?.filter((proposal) => proposal.isActive) || [];
        const minSellPrice =
            activeProposals.length > 0
                ? activeProposals.reduce(
                      (min, proposal) => Math.min(min, proposal.sellPrice),
                      Infinity,
                  )
                : 0;
        totalPrice = minSellPrice * itemQuantity;
    }

    const totalQuantity = inputQuantity !== null ? inputQuantity : itemQuantity;
    const totalAvailable = item?.proposals?.reduce(
        (acc, proposal) => acc + (proposal.availableQuantity || 0),
        0,
    );

    let stockDisplayText = totalAvailable
        ? formatNumber(totalAvailable)
        : t('Out');

    if (totalAvailable > 999) {
        stockDisplayText = t('In stock');
    }

    const isOutOfStock =
        item?.isCheckFinished &&
        totalAvailable <= 0 &&
        isPreSubmission &&
        showInventory;

    const totalAvailableForPicker = showInventory ? totalAvailable : 99999;

    const openProductDetails = useCallback(() => {
        if (!item.sku) return;
        onOpenProductDetails(item);
    }, [item, onOpenProductDetails]);

    const debounceQuantityChange = useDebouncedCallback(
        useCallback(
            async (quantity: number) => {
                try {
                    const dataToEdit = {
                        orderId: selectedOrderId,
                        itemId: item.id,
                        qty: quantity,
                    };

                    setItemUpdateInProgress(true);

                    await dispatch(editOrderItem(dataToEdit));

                    await dispatch(
                        fetchOrder({ id: selectedOrderId }),
                    ).unwrap();
                } catch (error) {
                    console.error('Save order item quantity failed:', error);
                    toast.error(t('Error updating item quantity'));
                    setInputQuantity(itemQuantity);
                } finally {
                    setItemUpdateInProgress(false);
                }
            },
            [dispatch, selectedOrderId, item.id, itemQuantity, t],
        ),
    );

    const handleQuantityChange = useCallback(
        (newQuantity: number) => {
            setInputQuantity(newQuantity);
            debounceQuantityChange(newQuantity);
        },
        [debounceQuantityChange],
    );

    return (
        <Box
            data-testclass="order-item"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                border: allSuppliersUnavailable
                    ? '1px solid #E9E8E8'
                    : '1px solid rgba(0, 0, 0, 0.15)',
                borderBottomWidth: '0px',
                padding: '20px',
                boxSizing: 'border-box',
                backgroundColor: allSuppliersUnavailable
                    ? '#F7F7F7'
                    : isOutOfStock
                    ? '#F6F6F6'
                    : 'transparent',
                '&:last-child': {
                    borderBottomWidth: '1px',
                    marginBottom: '20px',
                },
                '&:hover .order-item-change-category-hover, &:focus-within .order-item-change-category-hover':
                    {
                        opacity: 1,
                        pointerEvents: 'auto',
                    },
            }}
        >
            <Box
                sx={{
                    width: '100px',
                    height: '100px',
                    flex: '0 0 100px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginRight: '16px',
                    flexGrow: 0,
                    opacity: itemOpacity,
                }}
            >
                <Box
                    onClick={(e) => {
                        e.stopPropagation();
                        openLightbox(item.thumbnailUrl);
                    }}
                    sx={{
                        width: '100%',
                        height: '100%',
                        '&:hover': {
                            cursor: 'zoom-in',
                        },
                    }}
                >
                    <CatalogItemImage
                        src={item.thumbnailUrl}
                        showBorder={false}
                        height="100px"
                        width="100px"
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: showSellers
                        ? 'space-between'
                        : 'flex-start',
                    ...(showSellers && { minHeight: '100px' }),
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ flexGrow: 1 }}>
                        {substitutionTitle && (
                            <Typography
                                sx={{
                                    color: '#000',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    lineHeight: '20px',
                                    marginBottom: '10px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.2px',
                                }}
                            >
                                {substitutionTitle}
                            </Typography>
                        )}
                        <ProductRowTitleBlock
                            title={item.title}
                            brand={item.brand}
                            uom={item.uom}
                            qtyPerUom={item.qtyPerUom}
                            isAddedToCatalog={item.isAddedToCatalog}
                            interactive={Boolean(item.sku)}
                            onClickTitle={openProductDetails}
                            dimmed={
                                allSuppliersSubstituted ||
                                allSuppliersUnavailable
                            }
                            titleDataTestClass="order-item-title"
                            brandUomDataTestClass="order-item-brand-uom"
                        />
                    </Box>

                    {showInventory && (
                        <Box
                            sx={{
                                width: '70px',
                                minWidth: '70px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                flexGrow: 0,
                            }}
                        >
                            {areChecksRunning ? (
                                <Skeleton
                                    variant="rounded"
                                    animation="wave"
                                    width="40px"
                                    height="12px"
                                    sx={{
                                        ...skeletonSxProps,
                                    }}
                                />
                            ) : (
                                <Typography
                                    typography="openSans.body2"
                                    data-testclass="order-item-stock"
                                    sx={{
                                        color: isOutOfStock ? '#F00' : '#000',
                                        textAlign: 'right',
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                    }}
                                >
                                    {stockDisplayText}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box
                        sx={{
                            width: showPicker ? '120px' : '50px',
                            minWidth: showPicker ? '120px' : '50px',
                            display: 'flex',
                            justifyContent: showPicker ? 'center' : 'right',
                            marginLeft: '40px',
                            flexGrow: 0,
                            opacity: itemOpacity,
                        }}
                    >
                        {!showPicker ? (
                            <Typography
                                typography="openSans.body2"
                                data-testclass="order-item-qty"
                            >
                                {isPreSubmission
                                    ? totalQuantity
                                    : totalQtyFulfilled}
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    position: 'relative',
                                }}
                            >
                                {totalQuantity > totalAvailable &&
                                    !areChecksRunning &&
                                    showInventory && (
                                        <InfoOutlinedIcon
                                            sx={{
                                                fontSize: '16px',
                                                color: 'red',
                                                position: 'absolute',
                                                left: '-24px',
                                                top: '8px',
                                            }}
                                        />
                                    )}
                                <DynamicTooltip
                                    content={customSourcingTooltipContent(t)}
                                    disabled={!item.isSourcingOverridden}
                                >
                                    <OrderItemQuantityPicker
                                        value={totalQuantity}
                                        maxQuantity={totalAvailableForPicker}
                                        showDeleteButton={true}
                                        showLoading={itemUpdateInProgress}
                                        disabled={
                                            itemUpdateInProgress ||
                                            isOrderSubmissionLocked
                                        }
                                        showCustomQuantity={
                                            item.isSourcingOverridden
                                        }
                                        onQuantityChange={handleQuantityChange}
                                        onDelete={() => {
                                            removeItemRef.current?.triggerDelete();
                                        }}
                                    />
                                </DynamicTooltip>
                            </Box>
                        )}
                    </Box>

                    {showPrices && (
                        <Box
                            sx={{
                                width: '100px',
                                minWidth: '100px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                flexGrow: 0,
                                opacity: itemOpacity,
                            }}
                        >
                            {isPreSubmission ? (
                                <OrderPriceDisplay
                                    width="60px"
                                    containerHeight="24px"
                                    containerProps={{ fontWeight: 600 }}
                                    typography="openSans.body"
                                    testClass="order-item-total-price"
                                    price={totalPrice}
                                    orderHasLoadingPrices={areChecksRunning}
                                    order={selectedOrder}
                                />
                            ) : (
                                <OrderPriceDisplay
                                    width="60px"
                                    containerHeight="24px"
                                    containerProps={{ fontWeight: 600 }}
                                    typography="openSans.body"
                                    testClass="order-item-total-price"
                                    price={
                                        allSuppliersUnavailable
                                            ? 0
                                            : totalPriceFulfilled
                                    }
                                    orderHasLoadingPrices={areChecksRunning}
                                    order={selectedOrder}
                                    suppressEmptyPrice={
                                        !allSuppliersUnavailable
                                    }
                                    showZeroAsPrice={!isPreSubmission}
                                />
                            )}
                            <OrderItemSavings
                                itemSavingsResult={itemSavingsResult}
                                isPreSubmission={isPreSubmission}
                                canSeeOrderSavings={
                                    showPrices && inOrderSavingsVisible
                                }
                                orderId={selectedOrderId}
                                itemId={item.id}
                            />
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        display: showSellers ? 'flex' : 'none',
                        flexShrink: 0,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <OrderGroupItemsTableRowSuppliers
                            item={item}
                            removeItemRef={removeItemRef}
                        />
                    </Box>
                </Box>

                {!isPreSubmission && (
                    <OrderGroupItemsTableRowSubstitutions item={item} />
                )}
            </Box>
        </Box>
    );
};
